import React from 'react'
import { Link } from 'react-router-dom'
import { Button, Flex, H2, Line, SelectDropdown, Span, ToolTip } from '../../lib/components'
import { Pagination } from '../../lib/components/Pagination'
import { useBreakpoint } from '../../lib/utils/hooks'
import { SM } from '../../lib/utils/constants'
import { getTimestampWithTimezone, trimText } from '../../lib/utils/helpers'
import type { AdminOpportunityQuestion } from '../../services/opportunity-questions-service'
import './opportunity-questions.scss'

export type OpportunityQuestionFilters = {
  page?: number
  limit?: number
  opportunity_id?: string
  status?: string
}

function previewPlainTextFromHtml(html: string | null | undefined, maxLen: number): string {
  if (html === null || html === undefined || html === '') {
    return '—'
  }
  const text = new DOMParser().parseFromString(html, 'text/html').body.textContent ?? ''
  const trimmed = text.trim()
  if (trimmed === '') {
    return '—'
  }
  return trimText(trimmed, maxLen)
}

type OpportunityQuestionsProps = {
  opportunities: { text: string; value: string }[]
  isLoadingOpportunities: boolean
  questions: AdminOpportunityQuestion[]
  isLoading: boolean
  searchParams: OpportunityQuestionFilters
  statusList: { text: string; value: '' | 'pending' | 'answered' }[]
  canReply: boolean
  canViewReply: boolean
  pagination: {
    total: number
    limit: number
    count: number
    offset: number
    page: number
  }
  currentPage: number
  handleSearchParams: ({ name, value }: { name: string; value: string }) => void
  resetSearchParams: () => void
  handleCurrentPage: (currentPage: number) => void
}

export const OpportunityQuestionsPresenter = ({
  opportunities,
  isLoadingOpportunities,
  questions,
  isLoading,
  searchParams,
  statusList,
  canReply,
  canViewReply,
  pagination,
  currentPage,
  handleSearchParams,
  resetSearchParams,
  handleCurrentPage,
}: OpportunityQuestionsProps) => {
  const { breakpoint } = useBreakpoint()

  return (
    <Flex variant='column-center'>
      <H2 text='Opportunity FAQs' classes={['mb-48']} />
      <div className='opportunity-questions-toolbar-layout gap-12 mb-16 mt-16'>
        <SelectDropdown
          name='opportunity_id'
          id='opportunity-id-filter'
          formId='opportunity-faqs-filter'
          list={opportunities}
          value={searchParams.opportunity_id ? String(searchParams.opportunity_id) : ''}
          placeholder='All opportunities'
          handleChange={(val) => handleSearchParams({ name: 'opportunity_id', value: val })}
          dataTestId='opportunity-questions-opportunity-filter'
          isDisabled={isLoadingOpportunities}
        />
        <SelectDropdown
          name='status'
          id='status-filter'
          formId='opportunity-questions-filter'
          list={statusList}
          value={searchParams.status || ''}
          placeholder='All Status'
          handleChange={(val) => handleSearchParams({ name: 'status', value: val })}
          dataTestId='opportunity-questions-status-filter'
        />
        <div className='sort-reset'>
          <Button
            handleClick={() => resetSearchParams()}
            classes={['reset-btn']}
            variant='secondary-btn'
            text='Reset'
          />
        </div>
      </div>
      <Line />
      {!canViewReply ? (
        <p className='opportunity-questions__reply-note'>
          You have read-only access to this section. Replies are disabled.
        </p>
      ) : null}
      {!isLoading && questions.length === 0 ? (
        <Flex variant='column-center' classes={['mt-12']}>
          <Span classes={['center']} variant='thin' text='No opportunity FAQs found' />
        </Flex>
      ) : isLoading ? (
        <p className='opportunity-questions__loading'>Loading…</p>
      ) : (
        <div className='w-100'>
          <Flex variant='row' classes={['mb-24', 'table_scroll_horizontal']}>
            <table className='styled-table'>
              <thead>
                <tr>
                  <th>Opportunity</th>
                  <th>Asker</th>
                  <th>Question</th>
                  <th>Created on</th>
                  <th>Status</th>
                  <th>Existing answer</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {questions.map((question) => {
                  const answerPreview = previewPlainTextFromHtml(question.answer_text, 120)
                  const answerTooltip = previewPlainTextFromHtml(question.answer_text, 2000)
                  return (
                    <tr key={question.id}>
                      <td>
                        <span>
                          {question.opportunity_title || `Opportunity #${question.opportunity_id}`}
                        </span>
                      </td>
                      <td>
                        <div>{question.asker_name || '—'}</div>
                        <div className='opportunity-questions__muted'>
                          {question.asker_email || '—'}
                        </div>
                      </td>
                      <td>
                        <ToolTip content={question.question_text}>
                          <span>{trimText(question.question_text, 120)}</span>
                        </ToolTip>
                      </td>
                      <td>
                        {getTimestampWithTimezone(
                          new Date(question.created_at),
                          'Do MMM YYYY HH:mm:ss zzz',
                        )}
                      </td>
                      <td>{question.status}</td>
                      <td>
                        {question.answer_text ? (
                          <ToolTip content={answerTooltip}>
                            <span>{answerPreview}</span>
                          </ToolTip>
                        ) : (
                          <span>—</span>
                        )}
                      </td>
                      <td>
                        {canReply ? (
                          <Link
                            className='opportunity-questions__action-link'
                            to={`/faqs/opportunity-questions/${question.id}/reply`}
                            data-testid={`opportunity-questions-action-${question.id}`}
                          >
                            {question.status === 'answered' ? 'Edit answer' : 'Respond'}
                          </Link>
                        ) : (
                          <span className='opportunity-questions__muted'>—</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </Flex>
          {breakpoint && breakpoint === SM ? null : (
            <Pagination
              currentPage={currentPage}
              handleCurrentPage={handleCurrentPage}
              pagination={pagination}
            />
          )}
        </div>
      )}
    </Flex>
  )
}
